import { useState, useEffect } from 'react';
import { Calendar, Activity, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import api from '../../lib/api';

const fmtUK = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};


export default function SourceOverviewTab({ source, onRefresh }) {
  const [recentRuns, setRecentRuns] = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/feeds/sources/${source.id}/runs`, { params: { limit: 10 } });
        setRecentRuns(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [source.id]);

  const successCount = recentRuns.filter(r => r.status === 'success').length;
  const failedCount  = recentRuns.filter(r => r.status === 'failed').length;
  const successRate  = recentRuns.length ? Math.round((successCount / recentRuns.length) * 100) : null;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Source details */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h2 className="text-sm uppercase tracking-wider text-slate-400 font-semibold mb-4">Details</h2>
          <dl className="space-y-3 text-sm">
            <DetailRow label="Parser type" value={source.parser_type} />
            <DetailRow label="Frequency" value={`Every ${source.fetch_frequency_minutes} minutes`} />
            <DetailRow label="Priority" value={source.priority || 0} />
            <DetailRow label="Crawl mode" value={source.crawl_mode || 'standard'} />
            <DetailRow label="Organisation" value={source.primary_organisation_name || '—'} />
            {source.primary_person_name && <DetailRow label="Primary person" value={source.primary_person_name} />}
            <DetailRow label="Region" value={source.region || '—'} />
            <DetailRow label="Content licence" value={source.content_licence || '—'} />
            <DetailRow label="Created" value={fmtUK(source.created_at)} />
          </dl>
          {source.notes && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Notes</p>
              <p className="text-sm text-slate-700">{source.notes}</p>
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm uppercase tracking-wider text-slate-400 font-semibold">Recent activity</h2>
            {successRate !== null && (
              <span className={`text-xs font-semibold ${successRate >= 80 ? 'text-emerald-600' : successRate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                {successRate}% success
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-slate-400 text-sm">
              <Loader2 size={14} className="animate-spin" /> Loading...
            </div>
          ) : recentRuns.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">
              No fetch runs yet. Click "Fetch now" to ingest items.
            </p>
          ) : (
            <div className="space-y-2">
              {recentRuns.slice(0, 8).map(run => (
                <RunRow key={run.id} run={run} />
              ))}
            </div>
          )}
        </div>
      </div>

      {source.last_error_message && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-600 font-semibold mb-1">Last error</p>
              <p className="text-sm text-red-600 font-mono">{source.last_error_message}</p>
              {source.last_error_at && (
                <p className="text-xs text-red-400 mt-1">
                  {fmtUK(source.last_error_at)}
                  {source.consecutive_failures > 0 && ` • ${source.consecutive_failures} consecutive failure${source.consecutive_failures !== 1 ? 's' : ''}`}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function DetailRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-xs text-slate-400 uppercase tracking-wider font-semibold shrink-0 pt-0.5">{label}</dt>
      <dd className="text-sm text-slate-900 text-right">{value}</dd>
    </div>
  );
}


const RUN_STATUS = {
  success:     { colour: 'text-emerald-600', icon: CheckCircle },
  partial:     { colour: 'text-amber-600',   icon: AlertCircle },
  failed:      { colour: 'text-red-600',     icon: AlertCircle },
  in_progress: { colour: 'text-blue-600',    icon: Loader2 },
};


function RunRow({ run }) {
  const cfg = RUN_STATUS[run.status] || RUN_STATUS.failed;
  const Icon = cfg.icon;
  return (
    <div className="flex items-center gap-3 py-1.5 text-xs">
      <Icon size={12} className={`${cfg.colour} ${run.status === 'in_progress' ? 'animate-spin' : ''} shrink-0`} />
      <span className={`${cfg.colour} uppercase tracking-wider font-semibold w-16`}>{run.status}</span>
      <span className="text-slate-600">
        {run.items_found || 0} found, <span className="text-slate-900 font-medium">{run.new_items_created || 0} new</span>
      </span>
      <span className="text-slate-400 ml-auto">
        {fmtUK(run.fetch_started_at)}
      </span>
    </div>
  );
}